import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { fetchWithTimeout } from "@/core/lib/api-client";
import {
  apiErrorData,
  apiErrorStatus,
  extractApiExceptionCodeFromData,
} from "@/core/lib/api-errors";
import { consumeAuthRedirect } from "@/core/lib/auth-redirect";
import { setAuthMethod, setTokenExpiry } from "@/core/lib/auth-session";
import { jwtLoginMutation } from "@/openapi/@tanstack/react-query.gen";

export const useLogin = ({ onError }: { onError: (err: string) => void }) => {
  const navigate = useNavigate();

  return useMutation({
    ...jwtLoginMutation({ fetch: fetchWithTimeout(5000) }),
    onError: (err) => {
      // no status means the request never got a response at all
      if (apiErrorStatus(err) === undefined) {
        onError("Failed to connect to server");
        return;
      }

      const code = extractApiExceptionCodeFromData(apiErrorData(err) ?? null);
      if (code === "LOGIN_BAD_CREDENTIALS") {
        onError("Incorrect email or password");
        return;
      }

      if (code === "TOO_MANY_ATTEMPTS") {
        // surfaced by the global response interceptor toast
        return;
      }

      onError("Unknown server error");
    },
    onSuccess: async (data) => {
      setAuthMethod("jwt");
      if (data && "expires_in" in data) {
        setTokenExpiry(data.expires_in);
      }

      const redirectTo = consumeAuthRedirect();
      await navigate({ to: redirectTo ?? "/", replace: true });
    },
  });
};
